<script setup>
  import { computed, toRefs } from "vue";
  import { storeToRefs } from "pinia";
  import { Info } from "lucide-vue-next";
  import { Label } from "@/components/ui/label";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Input } from "@/components/ui/input";
  import { Checkbox } from "@/components/ui/checkbox";
  import { usePricerProjectStore } from "@stores/pricer/pricerProject";
  import TargetInputMain from "./InputMain.vue";
  import InputPerCountry from "./InputPerCountry.vue";
  import Countries from "./Countries.vue";
  import { formatInputValue, parseInputValue } from "@utils/formatters";

  const props = defineProps({ field: { type: Object, required: true } });
  const { field } = toRefs(props);

  const projectStore = usePricerProjectStore();
  const { selectedTarget: target } = storeToRefs(projectStore);

  const shouldHide = computed(() => {
    if (!target.value) return true;
    if (props.field.condition && typeof props.field.condition === "function") {
      return !props.field.condition(target.value);
    }
    if (!props.field.conditions) return false;
    return !props.field.conditions.every((condition) => {
      const value = condition.field.split(".").reduce((o, i) => (o ? o[i] : undefined), target.value);
      return value === condition.value;
    });
  });
</script>

<template>
  <div
    v-if="!shouldHide"
    class="mb-4">
    <div v-if="field.type === 'countries-table'">
      <Countries />
    </div>
    <div
      v-else
      class="flex items-end gap-12">
      <div class="flex-1 space-y-2">
        <div class="flex items-center justify-between">
          <div v-if="field.tooltip && !['none', 'checkbox'].includes(field.type)">
            <Tooltip :content="field.tooltip">
              <Label>
                {{ field.label }}
                <Info class="h-3.5 w-3.5 text-muted-foreground/70" />
              </Label>
            </Tooltip>
          </div>
          <Label v-else-if="!['none', 'checkbox'].includes(field.type)">{{ field.label }}</Label>
        </div>
        <div
          v-if="field.perCountry && target.countries.length > 1"
          class="flex items-center space-x-2 my-2">
          <Checkbox
            :id="`${field.name}-per-country`"
            v-model="target[`${field.name}_per_country`]" />
          <Label :for="`${field.name}-per-country`">Custom {{ field.label }} per country</Label>
        </div>
        <div class="mt-2">
          <TargetInputMain
            v-if="
              field.type !== 'multi-input' &&
              (!field.perCountry || !target[`${field.name}_per_country`] || target.countries.length === 1)
            "
            :field="field" />
          <div
            v-else
            :class="field.layout === 'row' ? 'flex gap-4' : 'space-y-4'">
            <TargetInputMain
              v-for="subField in field.fields"
              :key="subField.name"
              :field="subField"
              :class="{ 'flex-1': field.layout === 'row' }" />
          </div>
        </div>
      </div>
      <div class="flex items-center gap-4">
        <div class="w-20">
          <Input
            v-if="!field.hideAuto"
            v-model="target[`${field.name}_auto`]"
            class="text-center text-muted-foreground border-dashed border-1"
            readonly />
        </div>
        <div class="w-20">
          <Input
            v-if="!field.hideManual"
            :model-value="formatInputValue(target[`${field.name}_manual`])"
            class="text-center"
            @update:model-value="target[`${field.name}_manual`] = parseInputValue($event)" />
        </div>
      </div>
    </div>
    <div
      v-if="field.perCountry && target[`${field.name}_per_country`] && target.countries.length > 1"
      class="mt-4 space-y-2">
      <InputPerCountry
        v-for="countryData in target.countries"
        :key="countryData.country"
        :field="field"
        :country-data="countryData" />
    </div>
  </div>
</template>
